import { CheckOutlined, DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Flex, Input, InputNumber, Segmented, Select, Space, Typography, Upload } from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../lib/toast';
import { defaultDesignerName, hasAccountsAccess, hasDesignAccess, hasSalesAccess } from '../access';
import * as api from '../api';
import { Field, RequirementField } from '../components';
import { clearDraft, isMeaningfulDraft, loadDraft, saveDraft, type WorkOrderDraft } from '../drafts';
import { calendarDaysBetween, formatDate, formatDateTime, formatTime, parseDay, todayISO, workingDaysBetween } from '../format';
import { useHeldActions } from '../heldActions';
import { useZenBootstrap } from '../hooks';
import { hasQuoteAmount, isBlankItem, missingFields, prepareWorkOrderInput } from '../orderForm';
import type { Requirement, WorkOrder, WorkOrderInput } from '../types';
import { PageSkeleton } from '../ui';

const { Text } = Typography;
const AUTOSAVE_MS = 2_000;

const blankRequirement = (): Requirement => ({ required: false, details: '' });

function initialWorkOrderInput(existing: WorkOrder | undefined, creatorName: string): WorkOrderInput {
  if (!existing) {
    return {
      orderDate: todayISO(),
      customerType: 'b2c',
      details: '',
      items: [{ description: '', departmentIds: [] }],
      installation: blankRequirement(),
      transportation: blankRequirement(),
      repair: blankRequirement(),
      deliveryDate: todayISO(),
      deliveryAddress: '',
      contactPerson: creatorName,
    };
  }
  return {
    orderDate: existing.orderDate,
    clientId: existing.clientId,
    customerType: existing.customerType,
    details: existing.details,
    items: existing.items.map((item) => ({ id: item.id, description: item.description, departmentIds: item.departmentIds })),
    installation: existing.installation,
    transportation: existing.transportation,
    repair: existing.repair,
    deliveryDate: existing.deliveryDate,
    deliveryAddress: existing.deliveryAddress,
    contactPerson: existing.contactPerson,
  };
}

function deliveryHint(value: string): ReactNode {
  if (!value) return null;
  const today = new Date();
  const date = parseDay(value);
  const days = calendarDaysBetween(today, date);
  if (days < 0) return <span className="ua-text-danger">That date has already passed.</span>;
  if (days === 0) return 'Due today';
  const working = workingDaysBetween(today, date);
  return `Due ${formatDate(value)} · ${working} working ${working === 1 ? 'day' : 'days'}`;
}

interface Step {
  id: string;
  label: string;
  done: boolean;
  summary?: string;
}

export function WorkOrderFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data } = useZenBootstrap();
  const { announce } = useHeldActions();
  const existing = data?.workOrders.find((order) => order.id === id);
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [draftOffer, setDraftOffer] = useState<WorkOrderDraft | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const initializedFormKey = useRef<string | null>(null);
  const [input, setInput] = useState<WorkOrderInput>(() =>
    initialWorkOrderInput(existing, data?.currentUser ? defaultDesignerName(data.currentUser) : ''));
  const userId = data?.currentUser.id;
  const isNew = !id;

  useEffect(() => {
    if (!data) return;
    const formKey = id ?? 'new';
    if (initializedFormKey.current === formKey) return;
    setInput(initialWorkOrderInput(existing, defaultDesignerName(data.currentUser)));
    setDirty(false);
    setSavedAt(null);
    setDraftOffer(!id ? loadDraft(data.currentUser.id) : null);
    initializedFormKey.current = formKey;
  }, [data, existing, id]);

  // Autosave the new-order draft to this browser, two seconds after the last change.
  useEffect(() => {
    if (!isNew || !dirty || !userId || draftOffer) return;
    const timer = setTimeout(() => {
      if (!isMeaningfulDraft(input)) return;
      const at = new Date().toISOString();
      if (saveDraft(userId, { input, mode, savedAt: at })) setSavedAt(at);
    }, AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [dirty, draftOffer, input, isNew, mode, userId]);

  // Leaving with unsaved edits warns once (new drafts are already kept in the browser).
  useEffect(() => {
    if (!dirty || isNew) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, isNew]);

  if (!data) return <PageSkeleton />;
  if (id && !existing) {
    return <Alert type="error" showIcon title="Work order not found" description="It may have been deleted. Check the Deleted view in Work orders." />;
  }
  if (existing?.deletedAt || (existing?.status !== undefined && existing.status !== 'active')) {
    const deleted = Boolean(existing?.deletedAt);
    const cancelled = existing.status === 'cancelled';
    return (
      <Card>
        <Space orientation="vertical" size={16}>
          <Alert
            type={cancelled || deleted ? 'error' : 'info'}
            showIcon
            title={deleted ? 'Deleted work orders can’t be edited' : cancelled ? 'Cancelled work orders can’t be edited' : 'Completed work orders can’t be edited'}
            description={deleted
              ? 'The order is kept for audit history and is permanently read-only.'
              : cancelled
              ? 'Cancellation is permanent, so this work order is read-only.'
              : 'Reopen a completed department task before changing this work order.'}
          />
          <Button onClick={() => navigate(`/work-orders/${existing.id}`)}>View work order</Button>
        </Space>
      </Card>
    );
  }
  const selectable = data.departments.filter((department) => department.active && department.salesSelectable);
  const user = data.currentUser;
  const approvedOrder = Boolean(existing?.accountsApproved);
  const isAdmin = user.isAdmin;
  const isSales = hasSalesAccess(user);
  const canEditWorkflow = isAdmin || isSales || hasAccountsAccess(user);
  const canUpload = hasDesignAccess(user);
  const showQuote = !existing && (isSales || isAdmin);
  const patch = <K extends keyof WorkOrderInput>(key: K, value: WorkOrderInput[K]) => {
    setDirty(true);
    setInput((current) => ({ ...current, [key]: value }));
  };
  const patchItem = (index: number, next: WorkOrderInput['items'][number]) =>
    patch('items', input.items.map((item, itemIndex) => itemIndex === index ? next : item));

  const customerDone = Boolean(input.orderDate && (mode === 'existing' ? input.clientId : input.newCustomer?.name.trim()));
  const filledItems = input.items.filter((item) => !isBlankItem(item));
  const itemsDone = filledItems.length > 0 && filledItems.every((item) => item.description.trim() && item.departmentIds.length);
  const deliveryDone = Boolean(input.deliveryDate && input.contactPerson.trim());
  const hasQuote = hasQuoteAmount(input.quote);
  const missing = missingFields(input, mode);
  const isValid = missing.length === 0;
  const customerName = mode === 'existing'
    ? data.clients.find((client) => client.id === input.clientId)?.name
    : input.newCustomer?.name.trim();
  const steps: Step[] = [
    { id: 'wo-customer', label: 'Customer', done: customerDone, summary: customerName || undefined },
    { id: 'wo-items', label: 'Items', done: itemsDone, summary: `${filledItems.length} ${filledItems.length === 1 ? 'item' : 'items'}` },
    { id: 'wo-delivery', label: 'Delivery & requirements', done: deliveryDone, summary: input.deliveryDate ? `Due ${formatDate(input.deliveryDate)}` : undefined },
    ...(showQuote ? [{ id: 'wo-quote', label: 'Quote', done: hasQuote, summary: hasQuote ? 'Added' : 'Optional' }] : []),
  ];
  const currentStep = steps.find((step) => !step.done)?.id;

  const resumeDraft = () => {
    if (!draftOffer) return;
    setInput(draftOffer.input);
    setMode(draftOffer.mode);
    setSavedAt(draftOffer.savedAt);
    setDraftOffer(null);
  };
  const discardDraft = () => {
    clearDraft(user.id);
    setDraftOffer(null);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = prepareWorkOrderInput(input, mode);
      const saved = existing
        ? await api.updateWorkOrder(existing.id, payload)
        : await api.createWorkOrder(payload);
      for (const file of files) await api.uploadFile(saved.id, file);
      setDirty(false);
      if (existing) {
        toast.success(`Saved ${saved.orderNumber}`);
      } else {
        clearDraft(user.id);
        announce({
          message: <>Created <span className="ua-mono">{saved.orderNumber}</span> and sent it to Accounts</>,
          actionLabel: 'Create another',
          onAction: () => navigate('/work-orders/new'),
          durationMs: 8_000,
        });
      }
      navigate(`/work-orders/${saved.id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Couldn’t save the work order. Check the highlighted fields and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ua-form-layout">
      <nav className="ua-step-rail" aria-label="Work order sections">
        <div className="ua-step-rail-title">{existing ? 'Edit work order' : 'New work order'}</div>
        <ol>
          {steps.map((step, index) => (
            <li key={step.id} className={`ua-step${step.done ? ' is-done' : ''}${step.id === currentStep ? ' is-current' : ''}`}>
              <a href={`#${step.id}`} onClick={(event) => { event.preventDefault(); document.getElementById(step.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
                <span className="ua-step-index">{step.done ? <CheckOutlined /> : index + 1}</span>
                <span className="ua-step-copy">
                  <span className="ua-step-label">{step.label}</span>
                  {step.summary && <span className="ua-step-summary">{step.summary}</span>}
                </span>
              </a>
            </li>
          ))}
        </ol>
        {isNew && savedAt && <div className="ua-step-saved">Draft saved · {formatTime(savedAt)}</div>}
      </nav>

      <Space className="zen-work-order-form" orientation="vertical" size={16} style={{ width: '100%', maxWidth: 880 }}>
        {draftOffer && (
          <Alert
            type="info"
            showIcon
            title="You have an unfinished work order"
            description={`Saved in this browser · ${formatDateTime(draftOffer.savedAt)}`}
            action={(
              <Space>
                <Button type="primary" onClick={resumeDraft}>Resume draft</Button>
                <Button onClick={discardDraft}>Discard</Button>
              </Space>
            )}
          />
        )}

        <Card id="wo-customer" title="Customer">
          <Space orientation="vertical" size={16} style={{ width: '100%' }}>
            <Segmented
              disabled={approvedOrder}
              value={mode}
              onChange={(value) => {
                const next = value as 'existing' | 'new';
                setDirty(true);
                setMode(next);
                if (next === 'new') patch('clientId', undefined);
              }}
              options={[
                { value: 'existing', label: 'Existing customer' },
                { value: 'new', label: 'New customer' },
              ]}
            />
            <div className="form-grid">
              {mode === 'existing' ? (
                <Field label="Customer" required hint="Type a name or phone number">
                  <Select
                    disabled={approvedOrder}
                    showSearch
                    optionFilterProp="label"
                    value={input.clientId}
                    onChange={(value) => {
                      const client = data.clients.find((item) => item.id === value);
                      patch('clientId', value);
                      if (client && !input.deliveryAddress) patch('deliveryAddress', client.address);
                    }}
                    options={data.clients.map((client) => ({ value: client.id, label: `${client.name} · ${client.phone}` }))}
                    placeholder="Search customers"
                    style={{ width: '100%' }}
                  />
                </Field>
              ) : (
                <>
                  <Field label="Customer name" required><Input disabled={approvedOrder} value={input.newCustomer?.name} onChange={(event) => patch('newCustomer', { name: event.target.value, phone: input.newCustomer?.phone ?? '', address: input.newCustomer?.address ?? '' })} /></Field>
                  <Field label="Phone"><Input disabled={approvedOrder} value={input.newCustomer?.phone} placeholder="+91 98200 12345" onChange={(event) => patch('newCustomer', { name: input.newCustomer?.name ?? '', phone: event.target.value, address: input.newCustomer?.address ?? '' })} /></Field>
                  <Field label="Address" wide><Input.TextArea disabled={approvedOrder} rows={2} value={input.newCustomer?.address} onChange={(event) => patch('newCustomer', { name: input.newCustomer?.name ?? '', phone: input.newCustomer?.phone ?? '', address: event.target.value })} /></Field>
                </>
              )}
              <Field label="Customer type" required>
                <Select value={input.customerType} onChange={(value) => patch('customerType', value)} options={[{ value: 'b2c', label: 'Customer' }, { value: 'b2b', label: 'Supplier' }]} style={{ width: '100%' }} />
              </Field>
              <Field label="Work-order date" required><Input type="date" value={input.orderDate} onChange={(event) => patch('orderDate', event.target.value)} /></Field>
            </div>
          </Space>
        </Card>

        <Card id="wo-items" title="Items" extra={!approvedOrder ? (
          <Button icon={<PlusOutlined />} onClick={() => patch('items', [...input.items, { description: '', departmentIds: [] }])}>Add item</Button>
        ) : null}>
          <Space orientation="vertical" size={12} style={{ width: '100%' }}>
            {input.items.map((item, index) => (
              <Card
                key={item.id ?? index}
                size="small"
                className="ua-item-card"
                title={input.items.length > 1 && isBlankItem(item)
                  ? <>Item {index + 1} <Text type="secondary" style={{ fontWeight: 400 }}>· empty, won’t be saved</Text></>
                  : `Item ${index + 1}`}
                extra={!approvedOrder && input.items.length > 1 ? (
                  <Button type="text" aria-label={`Remove item ${index + 1}`} icon={<DeleteOutlined />} onClick={() => patch('items', input.items.filter((_, itemIndex) => itemIndex !== index))} />
                ) : null}
              >
                <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                  <Field label="Description" required>
                    <Input.TextArea
                      rows={4}
                      value={item.description}
                      onChange={(event) => patchItem(index, { ...item, description: event.target.value })}
                      placeholder="What to make, size, material and any production notes"
                    />
                  </Field>
                  <Field label="Departments" required hint="Design is added automatically.">
                    <Select
                      mode="multiple"
                      disabled={!canEditWorkflow}
                      value={item.departmentIds}
                      onChange={(departmentIds) => patchItem(index, { ...item, departmentIds })}
                      options={selectable.map((department) => ({ value: department.id, label: department.name }))}
                      placeholder="Choose one or more departments"
                      style={{ width: '100%' }}
                    />
                  </Field>
                </Space>
              </Card>
            ))}
          </Space>
        </Card>

        <Card id="wo-delivery" title="Delivery & requirements">
          <Space orientation="vertical" size={16} style={{ width: '100%' }}>
            <div className="form-grid">
              <Field label="Delivery date" required hint={deliveryHint(input.deliveryDate)}>
                <Input type="date" value={input.deliveryDate} onChange={(event) => patch('deliveryDate', event.target.value)} />
              </Field>
              <Field label="Contact / graphic designer" required>
                {isAdmin || isSales ? (
                  <Select
                    allowClear
                    value={input.contactPerson || undefined}
                    onChange={(value) => patch('contactPerson', value ?? '')}
                    options={data.designers.map((designer) => ({ value: designer.name, label: designer.name }))}
                    placeholder="Choose a designer"
                    style={{ width: '100%' }}
                  />
                ) : <Input disabled value={input.contactPerson} />}
              </Field>
              <Field label="Delivery address" wide hint="Optional">
                <Input.TextArea rows={2} value={input.deliveryAddress} onChange={(event) => patch('deliveryAddress', event.target.value)} />
              </Field>
            </div>
            <RequirementField label="Installation / delivery" value={input.installation} onChange={(value) => patch('installation', value)} />
            <RequirementField label="Repair" value={input.repair} onChange={(value) => patch('repair', value)} />
            {canUpload && (
              <Field label="Files" wide>
                <Upload
                  multiple
                  beforeUpload={(file) => {
                    setFiles((current) => [...current, file as unknown as File]);
                    return false;
                  }}
                  onRemove={(file) => {
                    setFiles((current) => current.filter((item) => item.name !== file.name));
                  }}
                  fileList={files.map((file, index) => ({ uid: String(index), name: file.name }))}
                >
                  <Button icon={<UploadOutlined />}>Choose files</Button>
                </Upload>
              </Field>
            )}
          </Space>
        </Card>

        {showQuote && (
          <Card id="wo-quote" title="Quote" extra={<Text type="secondary">Optional</Text>}>
            <div className="form-grid">
              <Field label="Quote amount (₹)">
                <InputNumber
                  min={0}
                  value={input.quote?.amount ?? 0}
                  onChange={(value) => patch('quote', { amount: Number(value) > 0 ? Number(value) : 0, notes: input.quote?.notes ?? '' })}
                  formatter={(value) => (value ? Number(value).toLocaleString('en-IN') : '')}
                  parser={(value) => Number((value ?? '').replace(/[^\d.]/g, ''))}
                  prefix="₹"
                  style={{ width: '100%' }}
                />
              </Field>
              <Field label="Short notes" wide hint={input.quote?.notes.trim() && !hasQuote ? 'Notes are saved only with a quote amount.' : undefined}><Input.TextArea rows={2} value={input.quote?.notes ?? ''} onChange={(event) => patch('quote', { amount: input.quote?.amount ?? 0, notes: event.target.value })} /></Field>
            </div>
          </Card>
        )}

        <Flex className="zen-form-footer ua-form-footer" justify="space-between" align="center" gap={16} wrap>
          <Text type="secondary">
            {isValid ? 'Ready to go.' : `Still needed: ${missing.join(', ')}.`}
          </Text>
          <Space>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
            <Button type="primary" disabled={!isValid} loading={saving} onClick={submit}>
              {existing ? 'Save changes' : 'Create & send to Accounts'}
            </Button>
          </Space>
        </Flex>
      </Space>
    </div>
  );
}
